import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import axios from 'axios';
import { useAuthStore } from './authStore';
import { authHeaders } from '../lib/apiAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PORTFOLIO_API = `${API}/portfolio`;

function portfolioUserId() {
  return useAuthStore.getState().user?.id || 'current-user';
}

// SAMPLE_HOLDINGS removed — production build must never ship a fake 20-stock portfolio.
// The "Load sample portfolio" CTA has been replaced with broker-connect + manual-add
// on the empty-state UI in PortfolioDashboardPage / HoldingsPage.

function _buildLocalOverview(holdings, prices = {}) {
  let totalValue = 0, totalInvested = 0, dayChangeTotal = 0;
  const enriched = holdings.map((h) => {
    const key = `${h.symbol}.${h.exchange === 'BSE' ? 'BO' : 'NS'}`;
    const p = prices[key];
    const cp = p?.currentPrice || h.avg_buy_price || h.avgBuyPrice || 0;
    const prevClose = p?.previousClose || cp;
    const qty = h.quantity || 0;
    const avg = h.avg_buy_price || h.avgBuyPrice || 0;
    const cv = cp * qty;
    const iv = avg * qty;
    const pnl = cv - iv;
    const dayChg = (cp - prevClose) * qty;
    totalValue += cv;
    totalInvested += iv;
    dayChangeTotal += dayChg;
    return {
      ...h,
      currentPrice: cp,
      previousClose: prevClose,
      currentValue: cv,
      investedValue: iv,
      unrealizedPnL: pnl,
      unrealizedPnLPercent: iv > 0 ? (pnl / iv) * 100 : 0,
      dayChange: dayChg,
      dayChangePercent: prevClose ? ((cp - prevClose) / prevClose) * 100 : 0,
      sector: h.sector || 'Others',
    };
  });

  enriched.forEach((e) => { e.weight = totalValue > 0 ? (e.currentValue / totalValue) * 100 : 0; });

  const sectorMap = {};
  enriched.forEach((e) => { sectorMap[e.sector] = (sectorMap[e.sector] || 0) + e.currentValue; });
  const sectorAllocation = Object.entries(sectorMap)
    .map(([sector, value]) => ({ sector, value, weight: totalValue > 0 ? (value / totalValue) * 100 : 0 }))
    .sort((a, b) => b.value - a.value);

  const sorted = [...enriched].sort((a, b) => b.currentValue - a.currentValue);
  const topHoldings = sorted.slice(0, 5).map((e) => ({
    symbol: e.symbol, companyName: e.company_name, value: e.currentValue, weight: e.weight,
  }));

  const riskFlags = [];
  if (enriched.length > 0) {
    const maxW = Math.max(...enriched.map((e) => e.weight));
    if (maxW > 25) riskFlags.push(`Single-stock exposure > 25% (${maxW.toFixed(1)}%)`);
  }
  if (sectorAllocation.length > 0 && sectorAllocation[0].weight > 60) {
    riskFlags.push(`Sector concentration > 60% (${sectorAllocation[0].sector} at ${sectorAllocation[0].weight.toFixed(1)}%)`);
  }
  if (enriched.length > 0 && enriched.length < 5) {
    riskFlags.push('Limited diversification (< 5 stocks)');
  }

  return {
    totalValue,
    totalInvested,
    totalPnL: totalValue - totalInvested,
    totalPnLPercent: totalInvested > 0 ? ((totalValue - totalInvested) / totalInvested) * 100 : 0,
    dayChange: dayChangeTotal,
    dayChangePercent: (totalValue - dayChangeTotal) > 0 ? (dayChangeTotal / (totalValue - dayChangeTotal)) * 100 : 0,
    holdingsCount: enriched.length,
    holdings: sorted,
    topHoldings,
    sectorAllocation,
    capAllocation: [],
    riskFlags,
    lastUpdated: new Date().toISOString(),
  };
}

function _periodToDays(period = '1M') {
  const map = { '1W': 7, '1M': 30, '3M': 90, '6M': 180, '1Y': 365, 'ALL': 730 };
  return map[period] || 30;
}

// _buildLocalPerformance removed — the prior smoothstep fallback produced a
// synthetic curve that misleads users when real snapshots haven't accumulated.
// The UI now shows an explicit "gathering data" empty state (see PerformancePage).
function _buildLocalPerformance(/* overview, holdings, period */) {
  return null;
}

function _fallbackFeedFromNewsInsights(news = [], insights = []) {
  const feedNews = news.map((n, i) => ({
    id: n.id || `news-${n.symbol || 'NA'}-${i}`,
    type: 'news',
    symbol: n.symbol || '',
    companyName: n.companyName || n.company_name || n.symbol || '',
    title: n.title || 'Portfolio news',
    body: n.summary || n.body || '',
    source: n.source || 'News',
    date: n.date || '',
    severity: 'low',
    url: n.url || '',
    metadata: {},
  }));

  const feedInsights = insights.map((it, i) => ({
    id: it.id || `insight-${it.symbol || 'NA'}-${i}`,
    type: it.type === 'insider_trade' ? 'insider' : (it.type || 'notice'),
    symbol: it.symbol || '',
    companyName: it.companyName || it.company_name || it.symbol || '',
    title: it.title || 'Portfolio insight',
    body: it.description || it.body || '',
    source: it.source || 'Market filing',
    date: it.date || '',
    severity: it.severity || 'medium',
    url: it.url || '',
    metadata: {},
  }));

  return [...feedNews, ...feedInsights].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}

function _toFeedDateValue(d) {
  if (!d) return 0;
  const ts = Date.parse(d);
  return Number.isNaN(ts) ? 0 : ts;
}

function _dedupeAndSortFeed(items = []) {
  const seen = new Set();
  const unique = [];
  for (const item of items) {
    const key = item.id || `${item.type || 'news'}|${item.symbol || ''}|${item.url || ''}|${item.title || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(item);
  }
  unique.sort((a, b) => _toFeedDateValue(b.date) - _toFeedDateValue(a.date));
  return unique;
}

async function _fetchPortfolioMarketNews(holdings = [], perSymbolLimit = 8) {
  const symbols = [...new Set((holdings || []).map((h) => h.symbol).filter(Boolean))];
  if (!symbols.length) return [];

  const companyMap = Object.fromEntries((holdings || []).map((h) => [h.symbol, h.company_name || h.companyName || h.symbol]));
  const responses = await Promise.allSettled(
    symbols.map((sym) => axios.get(`${API}/news/${encodeURIComponent(sym)}?limit=${perSymbolLimit}`))
  );

  const feed = [];
  responses.forEach((res, idx) => {
    if (res.status !== 'fulfilled') return;
    const symbol = symbols[idx];
    const items = res.value?.data?.data?.news || [];
    items.forEach((n, i) => {
      feed.push({
        id: `market-news-${symbol}-${n.id || i}`,
        type: 'news',
        symbol,
        companyName: companyMap[symbol] || symbol,
        title: n.title || 'Portfolio news',
        body: n.summary || n.full_content || '',
        source: n.source || 'Market News',
        date: n.date || '',
        severity: 'low',
        url: n.url || '',
        metadata: {},
      });
    });
  });

  return feed;
}

function _mapXSignalsToFeed(signals = []) {
  return (signals || []).map((s, i) => ({
    id: `x-signal-${s.id || i}`,
    type: 'x_post',
    symbol: s.symbol || '',
    companyName: s.companyName || s.symbol || '',
    title: `@${s.handle || s.author || 'x'}: ${(s.text || '').slice(0, 120)}`,
    body: s.text || '',
    source: s.handle ? `@${s.handle}` : (s.author || 'X'),
    date: s.date || '',
    severity: s.credibility && s.credibility > 0.7 ? 'medium' : 'low',
    url: s.handle ? `https://x.com/${s.handle}` : '',
    metadata: {
      likes: s.likes || 0,
      reposts: s.reposts || 0,
      credibility: s.credibility ?? null,
      tier: s.tier ?? null,
    },
  }));
}

export const usePortfolioStore = create(
  persist(
    (set, get) => ({
      holdings: [],
      brokerConnections: [],
      lastSynced: null,
      isLoading: false,
      isSyncing: false,
      error: null,

      overview: null,
      overviewLoading: false,
      overviewError: null,
      performance: null,
      performanceLoading: false,
      news: [],
      newsLoading: false,
      insights: [],
      insightsLoading: false,
      risk: null,
      riskLoading: false,
      alerts: [],
      alertsLoading: false,
      transactions: [],
      transactionsLoading: false,
      aiReview: null,
      aiReviewLoading: false,
      aiReviewError: null,
      aiReviewJobId: null,
      xSignals: [],
      xSignalsLoading: false,
      unifiedFeed: [],
      feedLoading: false,

      fetchHoldings: async () => {
        const current = get().holdings;
        set({ isLoading: true, error: null });
        try {
          const { data } = await axios.get(`${API}/portfolio/${portfolioUserId()}`);
          const remote = data.holdings || [];
          // only overwrite if remote has data; never wipe local holdings with empty
          if (remote.length > 0) {
            set({ holdings: remote, isLoading: false, lastSynced: new Date().toISOString() });
          } else {
            set({ isLoading: false });
          }
        } catch {
          set({ isLoading: false });
        }
      },

      addHolding: async (holding) => {
        const newHolding = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          user_id: portfolioUserId(),
          symbol: holding.symbol,
          company_name: holding.companyName || holding.company_name || holding.symbol,
          exchange: holding.exchange || 'NSE',
          sector: holding.sector || '',
          industry: holding.industry || '',
          quantity: holding.quantity,
          avg_buy_price: holding.avgBuyPrice || holding.avg_buy_price,
          first_buy_date: holding.firstBuyDate || holding.first_buy_date || '',
          source: holding.source || 'manual',
          created_at: new Date().toISOString(),
        };
        set((s) => ({ holdings: [...s.holdings, newHolding], lastSynced: new Date().toISOString() }));
        try {
          await axios.post(`${API}/portfolio/${portfolioUserId()}/holding`, {
            symbol: newHolding.symbol,
            company_name: newHolding.company_name,
            exchange: newHolding.exchange,
            quantity: newHolding.quantity,
            avg_buy_price: newHolding.avg_buy_price,
            first_buy_date: newHolding.first_buy_date,
            source: newHolding.source,
          });
        } catch { /* local-only is fine */ }
        return { status: 'success', holding: newHolding };
      },

      updateHolding: async (id, updates) => {
        set((s) => ({
          holdings: s.holdings.map((h) => (h.id === id ? { ...h, ...updates } : h)),
          lastSynced: new Date().toISOString(),
        }));
        try { await axios.put(`${PORTFOLIO_API}/${portfolioUserId()}/holding/${id}`, updates); } catch {}
      },

      deleteHolding: async (id) => {
        set((s) => ({
          holdings: s.holdings.filter((h) => h.id !== id),
          lastSynced: new Date().toISOString(),
        }));
        try { await axios.delete(`${API}/portfolio/${portfolioUserId()}/holding/${id}`); } catch {}
      },

      // Kept as a no-op so any lingering UI callsite doesn't crash. Real portfolios
      // must come from broker-connect or manual-add — never from a synthetic seed.
      seedSample: async () => {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[portfolioStore] seedSample() is a no-op — sample holdings have been removed.');
        }
      },

      fetchOverview: async () => {
        const holdings = get().holdings;
        if (!holdings.length) {
          set({ overview: null, overviewLoading: false });
          return;
        }
        set({ overviewLoading: true, overviewError: null });

        // try backend first
        try {
          const { data } = await axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/overview`);
          if (data.overview && data.overview.holdings?.length > 0) {
            set({ overview: data.overview, overviewLoading: false });
            return;
          }
        } catch {}

        // local fallback with live prices
        let prices = {};
        try {
          const symbols = holdings.map((h) => `${h.symbol}.${h.exchange === 'BSE' ? 'BO' : 'NS'}`);
          const { data: pd } = await axios.post(`${API}/stock-prices`, { symbols: symbols.slice(0, 20) });
          prices = pd || {};
        } catch {}

        set({ overview: _buildLocalOverview(holdings, prices), overviewLoading: false });
      },

      fetchPerformance: async (period = '1M') => {
        set({ performanceLoading: true });
        try {
          const { data } = await axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/performance?period=${period}`);
          if (data?.performance?.timeseries?.length) {
            set({ performance: data.performance, performanceLoading: false });
            return;
          }

          // snapshots may not exist yet; trigger backfill once and retry
          try {
            await axios.post(`${PORTFOLIO_API}/${portfolioUserId()}/backfill-history?days=${_periodToDays(period)}`);
            const { data: retry } = await axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/performance?period=${period}`);
            if (retry?.performance?.timeseries?.length) {
              set({ performance: retry.performance, performanceLoading: false });
              return;
            }
          } catch {}

          const localPerf = _buildLocalPerformance(get().overview, get().holdings, period);
          set({ performance: localPerf, performanceLoading: false });
        } catch {
          const localPerf = _buildLocalPerformance(get().overview, get().holdings, period);
          set({ performance: localPerf, performanceLoading: false });
        }
      },

      fetchNews: async () => {
        set({ newsLoading: true });
        try {
          const { data } = await axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/news`);
          set({ news: data.news || [], newsLoading: false });
        } catch { set({ newsLoading: false }); }
      },

      fetchInsights: async () => {
        set({ insightsLoading: true });
        try {
          const { data } = await axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/insights`);
          set({ insights: data.insights || [], insightsLoading: false });
        } catch { set({ insightsLoading: false }); }
      },

      fetchRisk: async () => {
        set({ riskLoading: true });
        try {
          const { data } = await axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/risk`);
          set({ risk: data.risk, riskLoading: false });
        } catch { set({ riskLoading: false }); }
      },

      fetchAlerts: async () => {
        set({ alertsLoading: true });
        try {
          const { data } = await axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/alerts`);
          set({ alerts: data.alerts || [], alertsLoading: false });
        } catch { set({ alertsLoading: false }); }
      },

      createAlert: async (alert) => {
        try {
          const { data } = await axios.post(`${PORTFOLIO_API}/${portfolioUserId()}/alerts`, alert);
          if (data.alert) set((s) => ({ alerts: [data.alert, ...s.alerts] }));
          return data;
        } catch { return null; }
      },

      deleteAlert: async (alertId) => {
        set((s) => ({ alerts: s.alerts.filter((a) => a.id !== alertId) }));
        try { await axios.delete(`${PORTFOLIO_API}/${portfolioUserId()}/alerts/${alertId}`); } catch {}
      },

      checkAlerts: async () => {
        try {
          const { data } = await axios.post(`${PORTFOLIO_API}/${portfolioUserId()}/alerts/check`);
          if (data.triggered?.length) await get().fetchAlerts();
          return data.triggered || [];
        } catch { return []; }
      },

      fetchTransactions: async () => {
        set({ transactionsLoading: true });
        try {
          const { data } = await axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/transactions`);
          set({ transactions: data.transactions || [], transactionsLoading: false });
        } catch { set({ transactionsLoading: false }); }
      },

      fetchAiReview: async () => {
        const rawHoldings = get().holdings;
        if (!rawHoldings.length) {
          set({ aiReviewError: 'No holdings found. Load your portfolio from the Overview tab first.' });
          return;
        }

        set({ aiReviewLoading: true, aiReviewError: null });

        const overviewHoldings = get().overview?.holdings;
        const holdings = overviewHoldings?.length ? overviewHoldings : rawHoldings;

        const payload = {
          holdings: holdings.map((h) => ({
            symbol: h.symbol,
            company_name: h.company_name || h.companyName || h.symbol,
            sector: h.sector || '',
            quantity: h.quantity || 0,
            avg_buy_price: h.avg_buy_price || h.avgBuyPrice || 0,
            current_price: h.currentPrice || h.current_price || 0,
          })),
        };

        // Step 1: start the async job with retry
        let jobId = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const { data } = await axios.post(`${PORTFOLIO_API}/${portfolioUserId()}/ai-review-local`, payload);
            jobId = data.job_id;
            if (jobId) break;
            if (data.message) { set({ aiReviewLoading: false, aiReviewError: data.message }); return; }
          } catch (err) {
            if (attempt === 2) {
              set({ aiReviewLoading: false, aiReviewError: 'Could not connect. If you have an ad blocker, please disable it for this site and retry.' });
              return;
            }
            await new Promise((r) => setTimeout(r, 1000));
          }
        }

        if (!jobId) { set({ aiReviewLoading: false, aiReviewError: 'Failed to start review' }); return; }

        // Step 2: poll every 3s
        for (let i = 0; i < 30; i++) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const { data: poll } = await axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/ai-review-status/${jobId}`);
            if (poll.status === 'done' && poll.review) {
              set({ aiReview: poll.review, aiReviewLoading: false });
              return;
            }
            if (poll.status === 'error') {
              set({ aiReviewLoading: false, aiReviewError: poll.message || 'Review failed' });
              return;
            }
          } catch {}
        }
        set({ aiReviewLoading: false, aiReviewError: 'Timed out. Please try again.' });
      },

      sendPortfolioChat: async (message) => {
        try {
          const holdings = get().holdings;
          const overviewH = get().overview?.holdings || [];
          const ctx = (overviewH.length ? overviewH : holdings)
            .map((h) => `${h.symbol}: ${h.quantity} shares, avg ₹${h.avg_buy_price || h.avgBuyPrice || 0}, current ₹${h.currentPrice || h.current_price || 0}, sector: ${h.sector || '?'}`)
            .join('\n');

          const response = await fetch(`${PORTFOLIO_API}/${portfolioUserId()}/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ message, holdings_context: ctx }),
          });

          const text = await response.text();
          const lines = text.split('\n').filter((l) => l.startsWith('data: '));
          let fullText = '';
          for (const line of lines) {
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'text') fullText += event.content;
              if (event.type === 'done' && event.full_text) fullText = event.full_text;
              if (event.type === 'error') fullText = event.message || 'An error occurred.';
            } catch {}
          }
          return { text: fullText || 'No response received. Please try again.' };
        } catch (err) {
          return { text: 'Unable to get AI response. Please try again.' };
        }
      },

      fetchXSignals: async (days = 7) => {
        set({ xSignalsLoading: true });
        try {
          const { data } = await axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/x-signals?days=${days}`);
          set({ xSignals: data.signals || [], xSignalsLoading: false });
        } catch { set({ xSignalsLoading: false }); }
      },

      fetchUnifiedFeed: async (types = 'all', days = 30) => {
        set({ feedLoading: true });
        const wanted = types === 'all'
          ? new Set(['news', 'x_post', 'insider', 'corporate_action', 'notice'])
          : new Set(String(types).split(',').map((t) => t.trim()).filter(Boolean));
        let holdings = get().holdings || [];
        if (!holdings.length) {
          try { await get().fetchHoldings(); } catch {}
          holdings = get().holdings || [];
        }
        if (!holdings.length) {
          try { await get().fetchOverview(); } catch {}
          const overviewHoldings = get().overview?.holdings || [];
          if (overviewHoldings.length) holdings = overviewHoldings;
        }

        try {
          const { data } = await axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/feed?types=${types}&days=${days}`);
          const unifiedFeed = data.feed || [];

          const extraFeedParts = [unifiedFeed];

          // Pull stock news from the broader market-news endpoint per portfolio holding.
          if (wanted.has('news') && holdings.length > 0) {
            try {
              const marketNews = await _fetchPortfolioMarketNews(holdings, 8);
              extraFeedParts.push(marketNews);
            } catch {}
          }

          // Ensure X content appears even if unified feed has gaps.
          if (wanted.has('x_post') && holdings.length > 0) {
            try {
              const { data: xData } = await axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/x-signals?days=${Math.min(days, 30)}`);
              const xFeed = _mapXSignalsToFeed(xData?.signals || []);
              extraFeedParts.push(xFeed);
            } catch {}
          }

          const merged = _dedupeAndSortFeed(extraFeedParts.flat());
          if (merged.length > 0) {
            set({ unifiedFeed: merged, feedLoading: false });
            return;
          }

          // fallback: combine legacy endpoints if unified feed is empty
          try {
            const [nRes, iRes] = await Promise.all([
              axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/news`),
              axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/insights`),
            ]);
            const fallback = _fallbackFeedFromNewsInsights(nRes.data?.news || [], iRes.data?.insights || []);
            set({ unifiedFeed: _dedupeAndSortFeed(fallback), feedLoading: false });
            return;
          } catch {}

          set({ unifiedFeed: [], feedLoading: false });
        } catch {
          try {
            const [nRes, iRes] = await Promise.all([
              axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/news`),
              axios.get(`${PORTFOLIO_API}/${portfolioUserId()}/insights`),
            ]);
            const fallback = _fallbackFeedFromNewsInsights(nRes.data?.news || [], iRes.data?.insights || []);
            set({ unifiedFeed: _dedupeAndSortFeed(fallback), feedLoading: false });
          } catch {
            set({ unifiedFeed: [], feedLoading: false });
          }
        }
      },

      importBrokerHoldings: async (brokerKey) => {
        try {
          const { data } = await axios.post(`${PORTFOLIO_API}/${portfolioUserId()}/import-broker?broker=${brokerKey}`);
          if (data.imported > 0) await get().fetchHoldings();
          return data;
        } catch { return { status: 'error', imported: 0 }; }
      },

      addBrokerConnection: (brokerName) => {
        set((s) => ({
          brokerConnections: [
            ...s.brokerConnections.filter((b) => b.brokerName !== brokerName),
            { id: `${Date.now()}`, userId: portfolioUserId(), brokerName, isConnected: true, connectedAt: new Date().toISOString() },
          ],
        }));
      },

      disconnectBroker: (brokerName) => {
        set((s) => ({ brokerConnections: s.brokerConnections.filter((b) => b.brokerName !== brokerName) }));
      },

      getHoldingsWithPrices: () => {
        const holdings = get().holdings;
        const totalValue = holdings.reduce((sum, h) => sum + ((h.currentPrice || h.avg_buy_price || h.avgBuyPrice || 0)) * (h.quantity || 0), 0);
        return holdings.map((holding) => {
          const currentPrice = holding.currentPrice || holding.avg_buy_price || holding.avgBuyPrice || 0;
          const qty = holding.quantity || 0;
          const avg = holding.avg_buy_price || holding.avgBuyPrice || 0;
          const currentValue = currentPrice * qty;
          const investedValue = avg * qty;
          const unrealizedPnL = currentValue - investedValue;
          const unrealizedPnLPercent = investedValue > 0 ? (unrealizedPnL / investedValue) * 100 : 0;
          const weight = totalValue > 0 ? (currentValue / totalValue) * 100 : 0;
          return {
            ...holding,
            companyName: holding.company_name || holding.companyName || holding.symbol,
            avgBuyPrice: avg,
            currentPrice, previousClose: holding.previousClose || currentPrice,
            currentValue, investedValue, unrealizedPnL, unrealizedPnLPercent,
            dayChange: (holding.dayChange || 0) * qty, dayChangePercent: holding.dayChangePercent || 0,
            weight,
          };
        });
      },

      getPortfolioStats: () => {
        const h = get().getHoldingsWithPrices();
        const tv = h.reduce((s, x) => s + x.currentValue, 0);
        const ti = h.reduce((s, x) => s + x.investedValue, 0);
        const pnl = tv - ti;
        const dc = h.reduce((s, x) => s + x.dayChange, 0);
        const sMap = new Map();
        h.forEach((x) => sMap.set(x.sector || 'Others', (sMap.get(x.sector || 'Others') || 0) + x.currentValue));
        return {
          totalValue: tv, totalInvested: ti, totalPnL: pnl,
          totalPnLPercent: ti > 0 ? (pnl / ti) * 100 : 0,
          dayChange: dc, dayChangePercent: tv > 0 ? (dc / (tv - dc)) * 100 : 0,
          holdings: h.length,
          topHoldings: [...h].sort((a, b) => b.currentValue - a.currentValue).slice(0, 5).map((x) => ({ symbol: x.symbol, companyName: x.companyName, value: x.currentValue, weight: x.weight })),
          sectorAllocation: Array.from(sMap.entries()).map(([sector, value]) => ({ sector, value, weight: tv > 0 ? (value / tv) * 100 : 0 })).sort((a, b) => b.value - a.value),
          riskFlags: [],
        };
      },

      clearPortfolio: () => {
        set({ holdings: [], brokerConnections: [], lastSynced: null, overview: null });
      },
    }),
    {
      name: 'portfolio-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        holdings: state.holdings,
        brokerConnections: state.brokerConnections,
        lastSynced: state.lastSynced,
      }),
    }
  )
);
